/* eslint-disable import/prefer-default-export */
import ky, { HTTPError, KyResponse, SearchParamsOption } from 'ky';
import isNil from 'lodash-es/isNil';
import toNumber from 'lodash-es/toNumber';
import store from 'store';
import { apiErrorSchema } from 'types/api/api-error';
import isHTTPError from 'utils/api/isHTTPError';
import OneBadRequest from 'utils/common/errors/OneBadRequest';
import OneBadResponseError from 'utils/common/errors/OneBadResponse';
import OneUnauthorizedError from 'utils/common/errors/OneUnauthorizedError';
import getApiHostname from 'utils/common/getApiHostname';

type SearchParams = SearchParamsOption;

const extractJsonBody = async (error: HTTPError) => {
  try {
    const apiError = await error.response.json();

    return apiError;
  } catch (_) {
    return undefined;
  }
};

type GetOptions = {
  searchParams?: SearchParams;
};
type PostOptions<Params> = {
  searchParams?: SearchParams;
  params?: Params;
};
type PutOptions<Params> = {
  searchParams?: SearchParams;
  params?: Params;
};
type PatchOptions<Params> = {
  searchParams?: SearchParams;
  params?: Params;
};
type DeleteOptions<Params> = {
  searchParams?: SearchParams;
  params?: Params;
};

const handleError = async (error: unknown) => {
  if (isHTTPError(error)) {
    if (toNumber(error.response.status) === 403) {
      if (error.request.url.endsWith('session/user')) {
        return;
      }

      throw new OneUnauthorizedError(error);
    }

    const bodyError = await extractJsonBody(error);

    const { success, data: apiError } = apiErrorSchema.safeParse(bodyError);

    if (success) {
      if (error.response.status === 422) {
        // 422 is Unprocessable Entity for the BE, meaning invalid input
        throw new OneBadRequest(apiError, error);
      }
    }
  }
};

const getData = async <Data>(response: KyResponse) => {
  try {
    if (response.headers.get('content-type')?.includes('json')) {
      return await response.json<Data>();
    }

    if (response.status >= 200 && response.status < 300) {
      // No JSON but successful response
      return null as unknown as Data;
    }

    // Nor JSON nor empty response, this is unsupported
    throw new OneBadResponseError({ response });
  } catch (e) {
    // supposed to be a JSON or Text but failed to parse
    throw new OneBadResponseError({ response }, e);
  }
};

type ApiFetcherOptions = {
  useAuth?: boolean;
  /**
   * Timeout in milliseconds
   */
  timeout?: number;
};

/**
 * Timeout set to 120000 milliseconds (2 minutes) to accommodate connection issues during Paris migration.
 *
 * TODO: Reduce timeout to 60000 milliseconds (1 minute) after Paris migration.
 * See https://i3dnet.atlassian.net/browse/UPG-11352 for more details.
 */
const DEFAULT_OPTIONS: ApiFetcherOptions = { useAuth: false, timeout: 120000 };

class ApiFetcher {
  api: typeof ky;

  options: ApiFetcherOptions;

  constructor(baserUrl = '', opts?: ApiFetcherOptions) {
    this.options = { ...DEFAULT_OPTIONS, ...opts };
    this.api = ky.create({
      prefixUrl: baserUrl,
      cache: 'no-store',
      timeout: this.options.timeout,
      credentials: this.options.useAuth ? 'include' : undefined,
    });
  }

  setUseAuth(useAuth: boolean) {
    this.options.useAuth = useAuth;
    this.api = this.api.extend({ credentials: useAuth ? 'include' : undefined });
  }

  private buildHeaders() {
    if (this.options.useAuth) {
      const { apiToken } = store.getState().authReducer.loginReducer;

      if (!isNil(apiToken)) {
        return {
          Authorization: `Bearer ${apiToken}`,
        };
      }
    }

    return undefined;
  }

  async get<Data>(relativeUrl: string, opts?: GetOptions) {
    try {
      const headers = this.buildHeaders();
      const response = await this.api.get(relativeUrl, { searchParams: opts?.searchParams, headers });

      const data = await getData<Data>(response);

      return { data, response };
    } catch (error) {
      await handleError(error);

      throw error;
    }
  }

  async post<Params, Data>(relativeUrl: string, opts?: PostOptions<Params>) {
    try {
      const headers = this.buildHeaders();
      const response = await this.api.post(relativeUrl, { json: opts?.params, searchParams: opts?.searchParams, headers });

      const data = await getData<Data>(response);

      return { data, response };
    } catch (error) {
      await handleError(error);

      throw error;
    }
  }

  async put<Params, Data>(relativeUrl: string, opts?: PutOptions<Params>) {
    try {
      const headers = this.buildHeaders();
      const response = await this.api.put(relativeUrl, { json: opts?.params, searchParams: opts?.searchParams, headers });

      const data = await getData<Data>(response);

      return { data, response };
    } catch (error) {
      await handleError(error);

      throw error;
    }
  }

  async patch<Params, Data>(relativeUrl: string, opts?: PatchOptions<Params>) {
    try {
      const headers = this.buildHeaders();
      const response = await this.api.patch(relativeUrl, { json: opts?.params, searchParams: opts?.searchParams, headers });

      const data = await getData<Data>(response);

      return { data, response };
    } catch (error) {
      await handleError(error);

      throw error;
    }
  }

  async delete<Params, Data>(relativeUrl: string, opts?: DeleteOptions<Params>) {
    try {
      const headers = this.buildHeaders();
      const response = await this.api.delete(relativeUrl, { json: opts?.params, searchParams: opts?.searchParams, headers });

      const data = await getData<Data>(response);

      return { data, response };
    } catch (error) {
      await handleError(error);

      throw error;
    }
  }
}

export const oneApi = new ApiFetcher(getApiHostname());
